/**
 * Nombre visible de un cliente. ÚNICA fuente de verdad.
 *
 * `first_name` y `last_name` son OPCIONALES en `UserI`: un walk-in cargado solo con
 * teléfono no tiene ninguno de los dos. Concatenarlos a mano (`${first} ${last}`)
 * imprime literalmente "undefined undefined" en la UI — pasaba en la card de la
 * agenda, en el hover y en las stats. Cualquier lugar que muestre el nombre de un
 * cliente usa esta función; si necesitás un fallback distinto, pasalo vos, no
 * escribas otra cascada.
 */
export interface NameableUser {
  first_name?: string | null;
  last_name?: string | null;
}

/** Etiqueta para un cliente sin nombre cargado. NO usamos email ni teléfono acá:
 *  son datos de contacto y su visibilidad depende del permiso `clients_view_all`. */
export const NO_CLIENT_NAME = 'Sin nombre';

/** Profesional del equipo sin «Puede ver el nombre de sus clientes» (`clients_name_view`): la API
 *  manda los nombres en `null`. Es otro caso que «Sin nombre» y NO cae al email ni al teléfono.
 *  El flag sale de `isClientNameHidden(session)` (`@/hooks/useClientPermissions`). */
export const HIDDEN_CLIENT_NAME = 'Nombre oculto';

/** Nombre que la API manda ya armado (avisos, historial). Falta —o llega en `null`— cuando el
 *  profesional no tiene «Ver nombre»: nunca imprimir «undefined» ni «null». */
export const cleanPersonName = (name?: string | null): string | undefined =>
  (name ?? '')
    .split(' ')
    .filter((part) => part && part !== 'null' && part !== 'undefined')
    .join(' ') || undefined;

/** Sujeto de un aviso («Un cliente reservó…») cuando no llega el nombre. */
export const NOTIFICATION_NO_CLIENT_NAME = 'Un cliente';

/** Nombre del cliente en un aviso (socket, bandeja, toast). */
export const notificationClientName = (name?: string | null): string =>
  cleanPersonName(name) ?? NOTIFICATION_NO_CLIENT_NAME;

/** Nombre completo, o `null` si el cliente no tiene ninguna parte del nombre cargada. */
export const resolveClientName = (user?: NameableUser | null, nameHidden = false): string | null => {
  if (nameHidden) return HIDDEN_CLIENT_NAME;
  if (!user) return null;
  const full = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
  return full.length > 0 ? full : null;
};

/** Nombre completo con fallback seguro para mostrar en una card / tile. */
export const displayClientName = (user?: NameableUser | null, nameHidden = false): string =>
  resolveClientName(user, nameHidden) ?? NO_CLIENT_NAME;

/** El selector de reservas agrega contacto sólo cuando existe y el acceso lo permite. */
export const bookingClientLabel = (
  user: (NameableUser & { email?: string | null; phone?: string | null }) | null | undefined,
  canViewContact: boolean
): string => {
  const name = displayClientName(user);
  if (!canViewContact) return name;
  const email = user?.email?.trim();
  const phone = user?.phone?.trim();
  const contact = email || (phone ? `+${phone.replace(/^\+/, '')}` : '');
  return contact ? `${name} - ${contact}` : name;
};

export interface BookingWithDisplayPerson {
  attendee?: NameableUser | null;
  user?: NameableUser | null;
}

/** En un turno para otra persona, quien asiste es el nombre operativo principal. */
export const resolveBookingClientName = (
  booking?: BookingWithDisplayPerson | null,
  nameHidden = false
): string | null =>
  nameHidden
    ? HIDDEN_CLIENT_NAME
    : (resolveClientName(booking?.attendee) ?? resolveClientName(booking?.user));

export const displayBookingClientName = (
  booking?: BookingWithDisplayPerson | null,
  nameHidden = false
): string => resolveBookingClientName(booking, nameHidden) ?? NO_CLIENT_NAME;

/** Nombre y apellido de cualquier persona (un profesional del equipo, quien canceló un turno):
 *  el apellido es opcional y, sin él, `${first} ${last}` imprimía «Ana null». */
export const fullName = (user?: NameableUser | null): string => resolveClientName(user) ?? '';
